import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchSignatures, deleteSignature, type EmailSignature } from '../../lib/inboxSignaturesApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

export default function InboxSignaturesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: signatures = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['inbox-signatures'],
    queryFn: fetchSignatures,
    // Cross-device freshness. `invalidateQueries` only refreshes the device that made the
    // change, and the inbox module emits no socket events, so a second device sat on its cached
    // list until the person refreshed by hand (seen: a signature added on a phone never appeared
    // on the open desktop tab). Polling only runs while the tab is visible — react-query's
    // `refetchIntervalInBackground` defaults to false — so an idle background tab costs nothing.
    // 15s, not 60: a minute is long enough that someone adding a signature on a phone and
    // turning to the desktop reads it as broken and refreshes by hand anyway.
    refetchInterval: 15_000,
    // Global staleTime is 60s (main.tsx:57), which would make a focus refetch a no-op for
    // the first minute. These lists are small and change rarely — always refetch.
    staleTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSignature,
    onSuccess: () => {
      setDeleteConfirm(null);
      qc.invalidateQueries({ queryKey: ['inbox-signatures'] });
    },
  });

  return (
    <div style={{
      maxWidth: 720, margin: '0 auto',
      padding: 'clamp(20px, 5vw, 40px) clamp(14px, 4vw, 24px)',
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      {/* Was an unlabelled arrow wired to a fixed destination. These pages are opened from the
          inbox, from the Email settings hub and from Settings, so a fixed target sent people
          somewhere they had never been. Back means back — same control as every other page. */}
      <BackButton onClick={() => navigate(-1)} />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            Signatures
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 0' }}>
            Professional signatures appended automatically to new messages and replies
          </p>
        </div>
        <button
          onClick={() => navigate('/barrister/inbox/settings/signatures/new')}
          style={{
            padding: '8px 18px', borderRadius: 6,
            background: '#1e3a5f', color: 'white',
            border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 500,
          }}
        >
          + New signature
        </button>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Loading…</div>
      ) : isError ? (
        /* A failed fetch would have read as "No signatures yet" — inviting someone to
           recreate a signature they already have. */
        <QueryErrorState what="your signatures" onRetry={() => void refetch()} />
      ) : signatures.length === 0 ? (
        <div style={{
          border: '1px dashed #d1d5db', borderRadius: 8,
          padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✍</div>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>No signatures yet</div>
          <div style={{ fontSize: 13 }}>Create a professional signature for your email messages.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {signatures.map((sig) => (
            <SignatureCard
              key={sig.id}
              sig={sig}
              deleteConfirm={deleteConfirm}
              onEdit={() => navigate(`/barrister/inbox/settings/signatures/${sig.id}/edit`)}
              onDeleteRequest={() => setDeleteConfirm(sig.id)}
              onDeleteConfirm={() => deleteMutation.mutate(sig.id)}
              onDeleteCancel={() => setDeleteConfirm(null)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SignatureCard({
  sig,
  deleteConfirm,
  onEdit,
  onDeleteRequest,
  onDeleteConfirm,
  onDeleteCancel,
}: {
  sig: EmailSignature;
  deleteConfirm: string | null;
  onEdit: () => void;
  onDeleteRequest: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}) {
  return (
    <div style={{
      border: '1px solid var(--border-default)', borderRadius: 8,
      background: 'var(--surface-card)', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
        padding: '12px 18px',
        borderBottom: '1px solid var(--border-subtle)',
      }}>
        <span style={{ fontWeight: 500, fontSize: 14, color: 'var(--text-primary)', flex: '1 1 160px', minWidth: 0 }}>
          {sig.name}
        </span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {sig.isDefaultNew && (
            <span style={{
              fontSize: 10, padding: '1px 7px', borderRadius: 10,
              background: '#dcfce7', color: '#166534',
            }}>
              default: new
            </span>
          )}
          {sig.isDefaultReply && (
            <span style={{
              fontSize: 10, padding: '1px 7px', borderRadius: 10,
              background: 'var(--surface-selected)', color: '#1d4ed8',
            }}>
              default: reply
            </span>
          )}
          {sig.isChambersBaseline && (
            <span style={{
              fontSize: 10, padding: '1px 7px', borderRadius: 10,
              background: '#fef9c3', color: '#854d0e',
            }}>
              chambers
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {!sig.isChambersBaseline && (
            <>
              <button
                onClick={onEdit}
                style={{
                  background: 'none', border: '1px solid var(--border-default)',
                  borderRadius: 4, padding: '8px 12px',
                  cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)',
                }}
              >
                Edit
              </button>
              {deleteConfirm === sig.id ? (
                <>
                  <button
                    onClick={onDeleteConfirm}
                    style={{
                      background: '#dc2626', color: 'white',
                      border: 'none', borderRadius: 4,
                      padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                    }}
                  >
                    Confirm
                  </button>
                  <button
                    onClick={onDeleteCancel}
                    style={{
                      background: 'none', border: '1px solid var(--border-default)',
                      borderRadius: 4, padding: '8px 12px',
                      cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)',
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={onDeleteRequest}
                  style={{
                    background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: 14, color: 'var(--text-subtle)',
                  }}
                >
                  ✕
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {/* Preview */}
      <div style={{
        padding: '12px 18px',
        fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6,
        background: '#fafafa',
        maxHeight: 120, overflow: 'hidden',
        borderBottom: '2px solid transparent',
        whiteSpace: 'pre-wrap',
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}>
        {sig.body}
      </div>
    </div>
  );
}
